import React, { useRef } from 'react';
import {
  Typography,
  Box,
  Container,
  IconButton,
  useTheme,
  useMediaQuery,
  Link
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { shades } from '../../../theme';
import doctorImage from '../../data/home/doctor.jpg';
import pharmImage from '../../data/home/pharm.jpg';
import hospitalImage from '../../data/home/hospital.avif';
import learnImage from '../../data/home/learn.jpg';
import finImage from '../../data/home/finance.avif';
import communityImage from '../../data/home/community.jpg';

const QuickServices = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery('(max-width:600px)');

  const services = [
    { id: 1, name: 'Find a Doctor', description: 'Easily connect with certified healthcare professionals', image: doctorImage, path: '/' },
    { id: 2, name: 'Pharmacies', description: 'Order and refill prescriptions from trusted pharmacies', image: pharmImage, path: '/' },
    { id: 3, name: 'Hospitals', description: 'Locate and access nearby hospitals and clinics', image: hospitalImage, path: '/' },
    { id: 4, name: 'Finance', description: 'Explore and manage healthcare financing options', image: finImage, path: '/' },
    { id: 5, name: 'Community', description: 'Engage with health forums and support groups', image: communityImage, path: '/' },
    { id: 6, name: 'Learn', description: 'Access reliable health information and resources', image: learnImage, path: '/' }
  ];

  const scrollRef = useRef(null);

  const handleScroll = (direction) => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const scrollAmount = direction === 'left' ? -300 : 300;

      // Get current scroll position
      const startPosition = container.scrollLeft;
      const targetPosition = startPosition + scrollAmount;

      container.scrollTo({
        left: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <Container maxWidth='xl' sx={{ py: 3, mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
        <Box>
          <Typography
            variant={isMobile ? 'body2' : 'body1'}
            sx={{
              fontWeight: 600,
              color: isDarkMode ? shades.white[100] : shades.secondary[500],
              backgroundColor: isDarkMode ? shades.neutral[800] : shades.neutral[200],
              display: 'inline-block',
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              mb: 1,
              fontSize: isMobile ? '0.7rem' : '0.75rem'
            }}
          >
            Find nearby
          </Typography>
          <Typography
            variant={isMobile ? 'h4' : 'h2'}
            sx={{ fontWeight: 600, color: isDarkMode ? shades.white[100] : shades.secondary[500] }}
          >
            Quick Find
          </Typography>
        </Box>
        <Box>
          <IconButton onClick={() => handleScroll('left')}>
            <ChevronLeftIcon />
          </IconButton>
          <IconButton onClick={() => handleScroll('right')}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>
      <Box
        ref={scrollRef}
        sx={{
          display: 'flex',
          gap: 3,
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          position: 'relative',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          pb: 2
        }}
      >
        {services.map((service) => (
          <Link
            key={service.id}
            href={service.path}
            underline='none'
            sx={{
              flex: '0 0 auto',
              width: isMobile ? 150 : 200,
              display: 'block'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: 2,
                cursor: 'pointer',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)'
                }
              }}
            >
              <Box
                sx={{
                  width: isMobile ? 100 : 160,
                  height: isMobile ? 100 : 160,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  mb: 2,
                  boxShadow: `0 4px 10px ${isDarkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.1)'}`,
                  position: 'relative'
                }}
              >
                <img
                  src={service.image}
                  alt={service.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.3s ease'
                  }}
                />
              </Box>
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 600,
                  mb: 0.5,
                  textAlign: 'center',
                  color: isDarkMode ? shades.white[100] : shades.secondary[600],
                  fontSize: isMobile ? '1rem' : '1.2rem'
                }}
              >
                {service.name}
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  textAlign: 'center',
                  color: isDarkMode ? shades.secondary[300] : shades.secondary[400],
                  fontSize: isMobile ? '0.75rem' : '0.875rem'
                }}
              >
                {service.description}
              </Typography>
            </Box>
          </Link>
        ))}
      </Box>
    </Container>
  );
};

export default QuickServices;
